import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import WeeklyChart from '../../components/charts/barChart/weeklyChart';
import DataItem from '../../components/dataItem';
import ProgressBar from '../../components/charts/progressBar';
import ChartSection from '../../components/charts/chartSection';
import BarChart from '../../components/charts/barChart';
import { formatDateTime } from '../../utils/base';
import { physicalRecordAPI } from '../../actions/imsServer';
import { EXERCISE_TARGETS, API_PAGE_SIZE } from '../../constants';

export const ReportStep = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    // API 데이터 상태
    const [physicalRecords, setPhysicalRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 사용자 정보 가져오기
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 선택된 날짜 상태
    const [selectedDate, setSelectedDate] = useState(new Date());

    // 선택된 시간대 상태
    const [selectedHourIndex, setSelectedHourIndex] = useState(-1);

    // 걸음수 데이터 가져오기
    const fetchStepData = async (startDate, endDate, isInitialLoad = false) => {
        if (!meInfo.id) return;

        try {
            if (isInitialLoad) {
                setLoading(true);
            }
            setError(null);

            const startStr = formatDateTime(startDate, '-', ':', 0, 10);
            const endStr = formatDateTime(endDate, '-', ':', 0, 10);
            let allRecords = [];
            let offset = 0;
            let page;
            do {
                const response = await physicalRecordAPI.getPhysicalRecords({
                    userId: meInfo.id,
                    state: 0,
                    startDate: startStr,
                    endDate: endStr,
                    offset
                });
                page = response?.data?.physicalRecords || [];
                allRecords = allRecords.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allRecords.length > 0) {
                // 기존 데이터와 병합 (중복 제거)
                setPhysicalRecords(prevRecords => {
                    const existingDates = new Set(prevRecords.map(r => formatDateTime(new Date(r.date), '-', ':', 0, 10)));
                    const uniqueNewRecords = allRecords.filter(r =>
                        !existingDates.has(formatDateTime(new Date(r.date), '-', ':', 0, 10))
                    );
                    return [...prevRecords, ...uniqueNewRecords].sort((a, b) =>
                        new Date(a.date) - new Date(b.date)
                    );
                });
            }
        } catch (err) {
            console.error('걸음수 데이터 로딩 실패:', err);
            setError(err.message);
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            }
        }
    };

    // 초기 데이터 로드 (컴포넌트 마운트 시)
    useEffect(() => {
        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 89); // 과거 3개월 (90일) - 더 많은 데이터 로드

        fetchStepData(startDate, today, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchStepData는 의존성에서 제외
    }, [meInfo.id]);

    // 스크롤로 새로운 구간 로드
    const loadMoreData = (direction) => {
        if (physicalRecords.length === 0) return;

        const sortedRecords = [...physicalRecords].sort((a, b) => new Date(a.date) - new Date(b.date));
        const earliestDate = new Date(sortedRecords[0].date);
        const latestDate = new Date(sortedRecords[sortedRecords.length - 1].date);

        if (direction === 'past') {
            // 과거 데이터 더 로드
            const newStartDate = new Date(earliestDate);
            newStartDate.setDate(newStartDate.getDate() - 14);
            const newEndDate = new Date(earliestDate);
            newEndDate.setDate(newEndDate.getDate() - 1);

            fetchStepData(newStartDate, newEndDate, false);
        } else if (direction === 'future') {
            // 미래 데이터 더 로드 (오늘까지만)
            const today = new Date();
            const newStartDate = new Date(latestDate);
            newStartDate.setDate(newStartDate.getDate() + 1);
            const newEndDate = new Date(Math.min(today.getTime(), latestDate.getTime() + 14 * 24 * 60 * 60 * 1000));

            if (newStartDate <= today) {
                fetchStepData(newStartDate, newEndDate, false);
            }
        }
    };

    // 주간 걸음수 데이터 생성 (1년치 고정)
    const weeklyData = useMemo(() => {
        const data = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const earliestDate = new Date(today);
        earliestDate.setDate(today.getDate() - 364);
        earliestDate.setHours(0, 0, 0, 0);
        const totalDays = 365;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(earliestDate);
            date.setDate(earliestDate.getDate() + i);

            const dateStr = formatDateTime(date, '/', ':', 0, 10);
            const record = physicalRecords.find(r => {
                const recordDate = new Date(r.date);
                recordDate.setHours(0, 0, 0, 0);
                return formatDateTime(recordDate, '/', ':', 0, 10) === dateStr;
            });

            const steps = record?.totalSteps || 0;
            const isCurrent = date.toDateString() === today.toDateString(); // 오늘이 현재 날짜
            const isTargetAchieved = steps >= EXERCISE_TARGETS.STEPS;

            data.push({
                day: dateStr,
                value: steps,
                isCurrent,
                color: isTargetAchieved ? theme.palette.figma.steps : theme.palette.figma.white[30]
            });
        }

        return data;
    }, [physicalRecords, theme.palette.figma.steps, theme.palette.figma.white]);

    // 시간별 걸음수 데이터 생성 (선택된 날짜 기준)
    const hourlyStepData = useMemo(() => {
        const selectedDateStr = formatDateTime(selectedDate, '/', ':', 0, 10);
        const record = physicalRecords.find(r => {
            const recordDate = new Date(r.date);
            return formatDateTime(recordDate, '/', ':', 0, 10) === selectedDateStr;
        });

        if (!record?.hourlySteps) {
            return Array(24).fill(0);
        }

        let hourlyStepsArray;
        if (typeof record.hourlySteps === 'string') {
            try {
                hourlyStepsArray = JSON.parse(record.hourlySteps);
            } catch (e) {
                console.error('hourlySteps 파싱 실패:', e);
                return Array(24).fill(0);
            }
        } else {
            hourlyStepsArray = record.hourlySteps;
        }

        return Array.isArray(hourlyStepsArray) ? hourlyStepsArray.map(step => parseInt(step) || 0) : Array(24).fill(0);
    }, [physicalRecords, selectedDate]);

    // 현재 걸음수 데이터 (선택된 날짜 기준)
    const currentStepData = useMemo(() => {
        const selectedDateStr = formatDateTime(selectedDate, '/', ':', 0, 10);
        const record = physicalRecords.find(r => {
            const recordDate = new Date(r.date);
            return formatDateTime(recordDate, '/', ':', 0, 10) === selectedDateStr;
        });

        return {
            steps: record?.totalSteps || 0,
            distance: record?.distance || 0,
            calories: record?.calories || 0
        };
    }, [physicalRecords, selectedDate]);

    return (
        <ContentArea sx={{ paddingBottom: '100px' }}>
            {/* Loading State */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1">{t('loading_data')}</Typography>
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="error">
                        {t('an_error_occurred_while_loading_data_message', { message: error })}
                    </Typography>
                </Box>
            )}

            {/* Content */}
            {!loading && !error && (
                <Box sx={{
                    width: '100%',
                    maxWidth: '100%',
                    margin: '0 auto',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    // 큰 화면에서도 적절한 너비 유지
                    '@media (min-width: 600px)': {
                        maxWidth: '600px',
                    },
                    '@media (min-width: 900px)': {
                        maxWidth: '800px',
                    },
                }}>
                    {/* 주간 걸음수 차트 */}
                    <WeeklyChart
                        data={weeklyData}
                        color={theme.palette.figma.steps}
                        height={50}
                        barWidth={10}
                        gap={28}
                        visibleItems={7} // 7개 바만 표시
                        startFromEnd={true} // 끝에서부터 표시 (오늘이 오른쪽 끝)
                        targetValue={EXERCISE_TARGETS.STEPS}
                        onDateSelect={(item) => {
                            const [year, month, day] = item.day.split('/');
                            const newSelectedDate = new Date(year, month - 1, day);
                            setSelectedDate(newSelectedDate);

                            // 다른 날짜를 클릭했을 때 시간별 차트의 팝업과 가이드라인 제거
                            setSelectedHourIndex(-1);
                        }}
                        onScrollToEnd={(direction) => {
                            loadMoreData(direction);
                        }}
                    />

                    {/* 걸음수 요약 */}
                    <CommonCard>
                        {/* 걸음수 숫자 */}
                        <DataItem value={currentStepData.steps} unit={t('didim')} size="large" sx={{ justifyContent: "center" }} />

                        {/* 진행률 바 */}
                        <ProgressBar
                            current={currentStepData.steps}
                            rangeStart={0}
                            rangeEnd={EXERCISE_TARGETS.STEPS}
                            start={0}
                            end={EXERCISE_TARGETS.STEPS}
                            height="30px"
                            borderRadius="20px"
                            progressColor={theme.palette.figma.steps}
                            showCurrentIndicator={false}
                            showProgress={true}
                            showStartEndValues={true}
                            startValue={0}
                            endValue={`${t('goal')}${EXERCISE_TARGETS.STEPS.toLocaleString()}`}
                            sx={{
                                width: '100%',
                                maxWidth: '300px',
                                minWidth: '200px',
                            }}
                        />

                        {/* 거리와 칼로리 */}
                        <Box sx={{
                            display: 'flex',
                            padding: '10px 0',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            alignSelf: 'stretch'
                        }}>
                            {/* 거리 */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '5px',
                                flex: '1 0 0',
                                borderRight: `1px solid ${theme.palette.figma.white[20]}`
                            }}>
                                <DataItem value={currentStepData.distance} unit="km" size="medium" sx={{ justifyContent: "center" }} />
                            </Box>

                            {/* 칼로리 */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '5px',
                                flex: '1 0 0',
                            }}>
                                <DataItem value={currentStepData.calories} unit="kcal" size="medium" sx={{ justifyContent: "center" }} />
                            </Box>
                        </Box>
                    </CommonCard>

                    {/* 시간별 걸음수 차트 */}
                    <CommonCard sx={{ alignItems: 'flex-start' }}>
                        <ChartSection
                            title={t('hourly_didim_count')}
                            renderChart={<BarChart
                                data={hourlyStepData}
                                color={theme.palette.figma.steps}
                                maxHeight={100}
                                currentDate={formatDateTime(selectedDate, '/', ':', 0, 10)}
                                clickable={true}
                                selectedHourIndex={selectedHourIndex}
                                onHourSelect={setSelectedHourIndex}
                            />}
                        />
                    </CommonCard>
                </Box>
            )}
        </ContentArea>
    );
};

export default ReportStep;